import React from 'react';
import { IFinancialPosition, IPositionMaterial, IPositionView } from '@sp-types/financialPositionType';
import { IDashboardDonutChartDataSet } from '../../../components/UI-Elements/DashBoardDonutCard';
import { routeColors } from '../../Policy/MaterialDetails/components/MaterialDetailUtils';
import { Material } from '../../../sp-types';
import { formatToPriceInDollars } from '../../../utils/FormatValue';
import { isSameMonth } from '../../../utils/exposureUtils';
import { MonthYear } from '../../../utils/MonthYear/monthYear';

export const positionsToDoughnut = (routeExposures: Record<string, ExposureObj>): IDashboardDonutChartDataSet[] =>
  Object.entries(routeExposures).map(([routeName, { exposureCalendar, color }], i) => ({
    fieldName: routeName,
    value: exposureCalendar[getDateStr(new Date())] || 0,
    color: color || routeColors[i % routeColors.length],
  }));

export const doughnutToMap = (
  values: IDashboardDonutChartDataSet[],
): Record<string, { totalValue: number; exposureCalendar: Record<string, number> }> =>
  values.reduce((acc, { fieldName, value: totalValue }) => ({ ...acc, [fieldName]: { totalValue } }), {});

export const materialsToDoughnut = (materials: IPositionMaterial[]): IDashboardDonutChartDataSet[] =>
  materials.map(({ materialName, totalPrice, color }) => ({
    fieldName: materialName,
    value: totalPrice,
    color: color || '',
    ratio: 0,
  }));

export const getRouteColorsMap = (positions?: IPositionView[]): Record<string, string> =>
  positions?.reduce((acc, p, i) => ({ ...acc, [p.routeName]: p.color || routeColors[i % routeColors.length] }), {}) ||
  {};

export const getDate = (fromDate: Date, addMonth: number): Date => {
  const totalMonths = fromDate.getMonth() + addMonth;
  const yearOffset = Math.floor(totalMonths / 12);
  const newMonth = ((totalMonths % 12) + 12) % 12; // Handle negative modulo correctly
  return new Date(fromDate.getFullYear() + yearOffset, newMonth, 20);
};

export const getMonthYear = (date: Date): MonthYear => ({ month: date.getMonth() + 1, year: date.getFullYear() });

export const getDateStr = (date: Date): string => `${date.getFullYear()}${1 + date.getMonth()}`;

type RouteDateVal = {
  routeName: string;
  value: number;
  dates: string[];
  color?: string;
};

export type MaterialDateVal = {
  materialId: string;
  value: number;
  dates: string[];
  color?: string;
};

export const getMonths = (startingDate: Date, monthSpread: number, filterFrom: Date) =>
  Array.from({ length: monthSpread })
    .map((_, i) => ({
      year: startingDate.getFullYear() + Math.floor((startingDate.getMonth() + i) / 12),
      month: ((startingDate.getMonth() + i) % 12) + 1,
    }))
    .filter(
      ({ year, month }) =>
        year > filterFrom.getFullYear() || (year === filterFrom.getFullYear() && month < filterFrom.getMonth() + 1),
    );

const positionToRouteDateVal = (
  { materialId, routeQuantity, routeName, createDate, expiryDate, positionType, color }: IPositionView,
  materialsMap: Record<string, Material>,
  hidePastDates: boolean = true,
): RouteDateVal => {
  const now = new Date();

  const monthCount = monthDiff(createDate.toDate(), expiryDate.toDate());
  const value = routeQuantity * (materialsMap[materialId]?.currentPrice || 1) * (positionType === 'long' ? 1 : -1);

  return {
    routeName,
    value,
    color,
    dates: Array.from({ length: monthCount })
      .map((_, i) => getDate(createDate.toDate(), i))
      .filter((a) => !hidePastDates || a > now || isSameMonth(a, now))
      .map(getDateStr),
  };
};

const generateMonthsArray = (positionGroups: IPositionView[], materialsMap: Record<string, Material>): RouteDateVal[] =>
  positionGroups.flatMap(({ positions }) => (positions || []).map((p) => positionToRouteDateVal(p, materialsMap)));

export const spreadMonthsArray = (materials: IPositionMaterial[]): MaterialDateVal[] =>
  materials
    .flatMap(({ positions }) => positions || [])
    .map(({ totalPrice, materialId, createDate, expiryDate, color }) => {
      const now = new Date();

      const monthCount = monthDiff(createDate.toDate(), expiryDate.toDate());
      return {
        materialId,
        value: totalPrice,
        dates: Array.from({ length: monthCount })
          .map((_, i) => getDate(createDate.toDate(), i))
          .filter((date) => date > now || isSameMonth(date, now))
          .map(getDateStr),
        color,
      };
    });

export interface ExposureObj {
  exposureCalendar: Record<string, number>;
  color?: string;
}
export const aggregatePositions = (
  positions: IPositionView[],
  materialsMap: Record<string, Material>,
): Record<string, ExposureObj> => {
  const positionsWithDates = generateMonthsArray(positions, materialsMap);

  const aggregatedRoutes: Record<string, ExposureObj> = {};

  positionsWithDates.forEach(({ routeName, value, dates, color }) => {
    aggregatedRoutes[routeName] = dates?.reduce(
      ({ exposureCalendar }, dateString) => ({
        exposureCalendar: { ...exposureCalendar, [dateString]: (exposureCalendar[dateString] || 0) + value },
        color,
      }),
      aggregatedRoutes[routeName] || ({ exposureCalendar: {} } as ExposureObj),
    );
  });

  return aggregatedRoutes;
};

const datesToObj = (dates: string[], value: number): { exposureCalendar: Record<string, number> } =>
  dates.reduce(({ exposureCalendar }, dateStr) => ({ exposureCalendar: { ...exposureCalendar, [dateStr]: value } }), {
    exposureCalendar: {},
  });

export const spreadMaterialsMonths = (materials: IPositionMaterial[]): Record<string, ExposureObj> => {
  const materialMonthValues = spreadMonthsArray(materials);

  const aggregatedRoutes: Record<string, ExposureObj> = {};

  materialMonthValues.forEach(({ materialId, value, dates }) => {
    aggregatedRoutes[materialId] = dates?.reduce(
      ({ exposureCalendar }, dateString) => ({
        exposureCalendar: { ...exposureCalendar, [dateString]: (exposureCalendar[dateString] || 0) + value },
      }),
      aggregatedRoutes[materialId] || ({ exposureCalendar: {} } as ExposureObj),
    );
  });
  return aggregatedRoutes;
};

export const monthDiff = (dateFrom: Date, dateTo: Date): number => {
  const months = (dateTo.getFullYear() - dateFrom.getFullYear()) * 12 + (dateTo.getMonth() - dateFrom.getMonth()) || 0;

  return months <= 0 ? 1 : months + 1;
};

const formatDate = (data: any) =>
  data?.value?.toDate().toLocaleDateString('en-US', { month: 'short', year: '2-digit' });

const formatPrice = (data: any) => formatToPriceInDollars(data?.value || 0);

const FormatDisabledPrice = ({ row, value }: any) => {
  return <div className={row.isDisabled ? 'font-gray-350' : ''}>{formatToPriceInDollars(value || 0)}</div>;
};

export const positionsRouteColumns = [
  { name: 'Route', value: 'routeName', width: 40 },
  { name: 'Created date', value: 'createDate', formatter: formatDate },
  { name: 'Expiry date', value: 'expiryDate', formatter: formatDate },
  { name: 'Route ID', value: 'routeId' },
  { name: 'Long / Short', value: 'positionType' },
  { name: 'Routes quantity', value: 'routeQuantity' },
  { name: 'Total price', value: 'totalPrice', formatter: FormatDisabledPrice, width: 40 },
];

export const positionsMaterialColumns = [
  { name: 'Material', value: 'materialName' },
  { name: 'Created date', value: 'createDate', formatter: formatDate },
  { name: 'Expiry date', value: 'expiryDate', formatter: formatDate },
  { name: 'Route ID', value: 'routeId' },
  { name: 'Routes quantity', value: 'routeQuantity' },
  { name: 'Total price', value: 'totalPrice', formatter: formatPrice },
];

export const FinancialMaterialSkeleton: React.FC = () => (
  <div className="flex flex-col gap-4">
    <div className="flex flex-col bg-white shadow-custom p-3 justify-between max-h-60">
      <div>
        <h5>Summary</h5>
      </div>
      <div className="flex flex-1 bg-gray-150 animate-pulse"></div>
      <div className="flex flex-1 bg-gray-150 animate-pulse"></div>
      <div className="flex flex-1 bg-gray-150 animate-pulse"></div>
    </div>
  </div>
);

export const addColors = (positions: IFinancialPosition[]): IPositionView[] =>
  positions.map((p, i) => ({ ...p, color: routeColors[i % routeColors.length] } as IPositionView));

export const FinancialPositionSkeleton: React.FC = () => (
  <div className="flex flex-col p-3 gap-4 bg-white">
    {Array.from({ length: 6 }).map((_, i: number) => (
      <div key={i} className={`${i === 1 ? 'h-64' : 'h-10'} bg-gray-100 animate-pulse rounded-md`}></div>
    ))}
  </div>
);
